import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { StreakReward } from '../../../api/API';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import { stripHtml } from '../../../util';
import { RootStoreContext } from '../../../stores/RootStore';
import { PopupPriority } from '../../../stores/PopupStore';
import './MilestoneCelebration.css';

interface MilestoneCelebrationProps {
    rewards: StreakReward[];
    userId: number;
    onViewRewards?: () => void;
}

const POPUP_ID = 'streak-reward-celebration';

const isPopupEligible = (reward: StreakReward) => (
    !reward.viewedAt &&
    !reward.redeemedAt &&
    reward.status !== 'redeemed' &&
    reward.status !== 'expired' &&
    (!reward.expiresAt || moment(reward.expiresAt).isAfter(moment()))
);

const MilestoneCelebration: React.FC<MilestoneCelebrationProps> = observer(({ rewards, userId, onViewRewards }) => {
    const { popupStore, registrationStore } = useContext(RootStoreContext);
    const [celebrateReward, setCelebrateReward] = useState<StreakReward | null>(null);
    const [isMarkingViewed, setIsMarkingViewed] = useState(false);

    useEffect(() => {
        if (!rewards || rewards.length === 0) return;

        const nextReward = rewards
            .filter(isPopupEligible)
            .sort((a, b) => new Date(b.achievedAt).getTime() - new Date(a.achievedAt).getTime())[0];

        setCelebrateReward(nextReward || null);
    }, [rewards, userId]);

    useEffect(() => {
        if (celebrateReward) {
            popupStore.register(POPUP_ID, PopupPriority.STREAK);
            return () => popupStore.unregister(POPUP_ID);
        }
    }, [celebrateReward, popupStore]);

    if (!celebrateReward) return null;
    if (!popupStore.isActive(POPUP_ID)) return null;

    const markViewedAndClose = async () => {
        if (isMarkingViewed) return;
        setIsMarkingViewed(true);
        await registrationStore.markStreakRewardViewed(celebrateReward.id);
        setCelebrateReward(null);
        setIsMarkingViewed(false);
    };

    const handleViewRewards = async () => {
        await markViewedAndClose();
        onViewRewards?.();
    };

    return (
        <div className="milestone-celebration-overlay">
            <div className="milestone-celebration-card">
                <div className="celebration-badge-icon">
                    <FontAwesomeIcon icon={faGift} />
                </div>
                <h2 className="celebration-headline">
                   {stripHtml(celebrateReward.rewardName || 'New streak reward')}
                </h2>
                <p className="celebration-body">
                    You earned this for your{' '}
                    <strong>{celebrateReward.milestoneWeek}&nbsp;{celebrateReward.milestoneWeek === 1 ? 'Week' : 'Weeks'}</strong> Dance Streak.
                </p>
                {celebrateReward.rewardDescription && (
                    <p className="celebration-reward-description">
                        {stripHtml(celebrateReward.rewardDescription)}
                    </p>
                )}
                {celebrateReward.expiresAt && (
                    <p className="celebration-expiry">
                        Expires {moment(celebrateReward.expiresAt).format('DD.MM.YYYY')}
                    </p>
                )}
                <div className="celebration-actions">
                    <button
                        className="celebration-dismiss-btn"
                        onClick={handleViewRewards}
                        disabled={isMarkingViewed}
                    >
                        View my rewards
                    </button>
                    <button
                        className="celebration-close-btn"
                        onClick={markViewedAndClose}
                        disabled={isMarkingViewed}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
});

export default MilestoneCelebration;
